import React, { useState, useEffect } from 'react';
import Header from './header';
import Footer from './footer';
import BannerRequestForm from './bannerRequestForm';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';

const BASE_URL = "https://admin.cappadociavisitor.com";

const ContactForm = () => {
  const { t } = useTranslation();
  const generalSettingsData = useSelector(state => state.settingsApi.generalSettingsApi.data)

  // Force scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check localStorage flag
    const shouldScrollToTop = localStorage.getItem('scrollToTop');
    if (shouldScrollToTop === 'true') {
      window.scrollTo(0, 0);
      localStorage.removeItem('scrollToTop'); // Clear the flag
    }
  }, []);

  const getValue = (key) => {
    if (!generalSettingsData) {
        return 'Loading...';
    }
    const foundItem = generalSettingsData.find((item) => item.key === key);
    return foundItem ? foundItem.value : 'Not found';
};
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    country: '',
    request: ''
  });
  
  // Form validation
  const [errors, setErrors] = useState({});
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = t('requestForm.required');
    }
    
    // Validate surname
    if (!formData.surname.trim()) {
      newErrors.surname = t('requestForm.required');
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = t('requestForm.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('requestForm.invalidEmail');
    }
    
    // Validate phone
    if (!formData.phone.trim()) {
      newErrors.phone = t('requestForm.required');
    } else if (!/^\+?[0-9\s\-()]{8,20}$/.test(formData.phone)) {
      newErrors.phone = t('requestForm.invalidPhone');
    }
    
    // Validate request description
    if (!formData.request.trim()) {
      newErrors.request = t('requestForm.required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BASE_URL}/api/v1/contact`, formData);
      
      if (response.status === 200 || response.status === 201) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          surname: '',
          email: '',
          phone: '',
          country: '',
          request: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };
  
  return (
    <div className="container-fluid w-100 h-100 p-0">
      <Header />
      <BannerRequestForm />
      
      <div className="request-form-background py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="request-form-container">
                <div className="form-content">
                  <div className="form-header">
                    <h2>{t('requestForm.title')}</h2>
                    <p>{t('requestForm.subtitle')}</p>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="success-message">
                      <div className="success-icon">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <p>{t('requestForm.successMessage')}</p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="error-message">
                      <div className="error-icon">
                        <i className="fas fa-exclamation-circle"></i>
                      </div>
                      <p>{t('requestForm.errorMessage')}</p>
                    </div>
                  )}
                  
                  <Form onSubmit={handleSubmit} className="luxury-form">
                    <Row>
                      <Col md={6}>
                        <div className="form-floating custom-form-group">
                          <input
                            type="text"
                            id="name"
                            name="name"
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            placeholder={t('requestForm.placeholder.name')}
                            value={formData.name}
                            onChange={handleChange}
                          />
                          <label htmlFor="name">{t('requestForm.name')}</label>
                          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                      </Col>
                      
                      <Col md={6}>
                        <div className="form-floating custom-form-group">
                          <input
                            type="text"
                            id="surname"
                            name="surname"
                            className={`form-control ${errors.surname ? 'is-invalid' : ''}`}
                            placeholder={t('requestForm.placeholder.surname')}
                            value={formData.surname}
                            onChange={handleChange}
                          />
                          <label htmlFor="surname">{t('requestForm.surname')}</label>
                          {errors.surname && <div className="invalid-feedback">{errors.surname}</div>}
                        </div>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <div className="form-floating custom-form-group">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            placeholder={t('requestForm.placeholder.email')}
                            value={formData.email}
                            onChange={handleChange}
                          />
                          <label htmlFor="email">{t('requestForm.email')}</label>
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                      </Col>
                      
                      <Col md={6}>
                        <div className="form-floating custom-form-group">
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            placeholder={t('requestForm.placeholder.phone')}
                            value={formData.phone}
                            onChange={handleChange}
                          />
                          <label htmlFor="phone">{t('requestForm.phone')}</label>
                          {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                      </Col>
                    </Row>

                    <Col md={12}>
                      <div className="form-floating custom-form-group">
                        <input
                          type="text"
                          id="country"
                          name="country"
                          className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                          placeholder={t('requestForm.placeholder.country')}
                          value={formData.country}
                          onChange={handleChange}
                        />
                        <label htmlFor="country">{t('requestForm.country')}</label>
                        {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                      </div>
                    </Col>
                    
                    
                    <div className="form-floating custom-form-group">
                      <textarea
                        id="request"
                        name="request"
                        className={`form-control ${errors.request ? 'is-invalid' : ''}`}
                        placeholder={t('requestForm.placeholder.request')}
                        value={formData.request}
                        onChange={handleChange}
                        style={{ height: '160px' }}
                      ></textarea>
                      <label htmlFor="request">{t('requestForm.request')}</label>
                      {errors.request && <div className="invalid-feedback">{errors.request}</div>}
                    </div>
                    
                    <div className="submit-btn-container">
                      <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner"></span>
                            <span className="ms-2">{t('requestForm.submit')}...</span>
                          </>
                        ) : (
                          t('requestForm.submit')
                        )}
                      </button>
                    </div>
                  </Form>
                </div>
                
                <div className="contact-details">
                  <div className="contact-item">
                    <div className="icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div className="info">
                      <h4>Email</h4>
                      <p>info@cappadociavisitor.com</p>
                    </div>
                  </div>
                  
                  <div className="contact-item">
                    <div className="icon">
                      <i className="fas fa-phone-alt"></i>
                    </div>
                    <div className="info">
                      <h4>{t('footer.phone')}</h4>
                      <p>{getValue("phone")}</p>
                    </div>
                  </div>
                  
                  <div className="contact-item">
                    <div className="icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div className="info">
                      <h4>{t('footer.address')}</h4>
                      <p>{getValue("address")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Custom CSS for luxury form */}
      <style jsx="true">{`
        .request-form-background {
          background-color: #f9f9f9;
          min-height: 600px;
        }
        
        .request-form-container {
          display: flex;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          margin-top: -60px;
          position: relative;
          z-index: 10;
        }
        
        .form-content {
          flex: 2;
          padding: 3.5rem;
          position: relative;
        }
        
        .contact-details {
          flex: 1;
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          padding: 3.5rem 2rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        
        .form-header h2 {
          font-size: 2.2rem;
          color: #1e3c72;
          font-weight: 600;
          margin-bottom: 0.8rem;
        }
        
        .form-header p {
          color: #666;
          font-size: 1.1rem;
          margin-bottom: 2.5rem;
        }
        
        .custom-form-group {
          margin-bottom: 1.8rem;
        }
        
        .form-control {
          height: calc(3.5rem + 2px);
          padding: 1.2rem 1rem;
          font-size: 1rem;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          transition: all 0.3s ease;
        }
        
        .form-control:focus {
          border-color: #1e3c72;
          box-shadow: 0 0 0 0.2rem rgba(30, 60, 114, 0.15);
        }
        
        .form-control.is-invalid {
          border-color: #dc3545;
        }
        
        textarea.form-control {
          min-height: 160px;
          resize: none;
        }
        
        .form-floating > label {
          padding: 1.2rem 1rem;
          color: #888;
        }
        
        .submit-btn-container {
          margin-top: 2.5rem;
          text-align: center;
        }
        
        .submit-btn {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          border: none;
          padding: 0.8rem 2.5rem;
          font-size: 1.1rem;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          min-width: 200px;
          box-shadow: 0 4px 15px rgba(30, 60, 114, 0.2);
        }
        
        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 7px 20px rgba(30, 60, 114, 0.25);
        }
        
        .submit-btn:active {
          transform: translateY(0);
        }
        
        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .spinner {
          display: inline-block;
          width: 1.2rem;
          height: 1.2rem;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .success-message,
        .error-message {
          display: flex;
          align-items: center;
          padding: 1rem 1.5rem;
          border-radius: 6px;
          margin-bottom: 2rem;
        }
        
        .success-message {
          background-color: rgba(25, 135, 84, 0.1);
          border-left: 4px solid #198754;
          color: #198754;
        }
        
        .error-message {
          background-color: rgba(220, 53, 69, 0.1);
          border-left: 4px solid #dc3545;
          color: #dc3545;
        }
        
        .success-icon,
        .error-icon {
          font-size: 1.5rem;
          margin-right: 1rem;
        }
        
        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 2.5rem;
        }
        
        .contact-item .icon {
            width: 3rem;
    min-width: 3rem;
    height: 3rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.2rem;
          font-size: 1.2rem;
        }
        
        .contact-item .info h4 {
          font-size: 1.1rem;
          margin-bottom: 0.2rem;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .contact-item .info p {
          margin: 0;
          font-size: 1.05rem;
          color: white;
        }
        
        /* Responsive adjustments */
        @media (max-width: 991.98px) {
          .request-form-container {
            flex-direction: column;
            margin-top: -30px;
          }
          
          .form-content {
            padding: 2.5rem;
          }
        }
        
        @media (max-width: 767.98px) {
          .form-content {
            padding: 2rem 1.5rem;
          }
          
          .form-header h2 {
            font-size: 1.8rem;
          }
        }
      `}</style>
      
      <Footer />
    </div>
  );
};

export default ContactForm; 